import { useState, useEffect } from 'react';
import { supabase } from '../../lib/supabase';
import { useAuth } from '../../contexts/AuthContext';
import Navbar from '../../components/feature/Navbar';
import Footer from '../../components/feature/Footer';

interface Event {
  id: string;
  title: string;
  description: string;
  event_type: 'attendance' | 'participation' | 'special';
  talent_reward: number;
  start_date: string;
  end_date: string;
  max_participants: number | null;
  current_participants: number;
}

interface Participation {
  event_id: string;
}

export default function EventsPage() {
  const { user } = useAuth();
  const [events, setEvents] = useState<Event[]>([]);
  const [participations, setParticipations] = useState<Participation[]>([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState<'all' | 'attendance' | 'participation' | 'special'>('all');

  useEffect(() => {
    fetchEvents();
    if (user) {
      fetchParticipations();
    }
  }, [user]);

  const fetchEvents = async () => {
    try {
      const { data, error } = await supabase
        .from('events')
        .select('*')
        .eq('is_active', true)
        .gte('end_date', new Date().toISOString())
        .order('start_date', { ascending: true });

      if (error) throw error;
      setEvents(data || []);
    } catch (error) {
      console.error('이벤트 로딩 실패:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchParticipations = async () => {
    try {
      const { data, error } = await supabase
        .from('event_participations')
        .select('event_id')
        .eq('user_id', user?.id);

      if (error) throw error;
      setParticipations(data || []);
    } catch (error) {
      console.error('참여 기록 로딩 실패:', error);
    }
  };

  const handleParticipate = async (event: Event) => {
    if (!user) {
      alert('로그인이 필요합니다.');
      window.REACT_APP_NAVIGATE('/auth/login');
      return;
    }

    if (hasParticipated(event.id)) {
      alert('이미 참여한 이벤트입니다.');
      return;
    }

    if (event.max_participants && event.current_participants >= event.max_participants) {
      alert('참여 인원이 마감되었습니다.');
      return;
    }

    try {
      // 참여 기록 생성
      const { error: participationError } = await supabase
        .from('event_participations')
        .insert([{
          event_id: event.id,
          user_id: user.id,
          talent_earned: event.talent_reward,
        }]);

      if (participationError) throw participationError;

      // 달란트 지급
      const { error: pointError } = await supabase
        .from('point_transactions')
        .insert([{
          user_id: user.id,
          amount: event.talent_reward,
          type: 'earn',
          description: `이벤트 참여: ${event.title}`,
        }]);

      if (pointError) throw pointError;

      // 사용자 달란트 업데이트
      const { data: profileData } = await supabase
        .from('profiles')
        .select('talent_points')
        .eq('id', user.id)
        .single();

      if (profileData) {
        await supabase
          .from('profiles')
          .update({ talent_points: (profileData.talent_points || 0) + event.talent_reward })
          .eq('id', user.id);
      }

      // 참여자 수 증가
      await supabase
        .from('events')
        .update({ current_participants: event.current_participants + 1 })
        .eq('id', event.id);

      alert(`참여 완료! ${event.talent_reward} 달란트가 적립되었습니다.`);
      fetchEvents();
      fetchParticipations();
    } catch (error) {
      console.error('참여 실패:', error);
      alert('참여에 실패했습니다.');
    }
  };

  const hasParticipated = (eventId: string) => {
    return participations.some(p => p.event_id === eventId);
  };

  const getEventTypeLabel = (type: string) => {
    const labels = {
      attendance: '출석',
      participation: '참여',
      special: '특별 이벤트',
    };
    return labels[type as keyof typeof labels] || type;
  };

  const getEventTypeColor = (type: string) => {
    const colors = {
      attendance: 'bg-blue-100 text-blue-800',
      participation: 'bg-green-100 text-green-800',
      special: 'bg-purple-100 text-purple-800',
    };
    return colors[type as keyof typeof colors] || 'bg-gray-100 text-gray-800';
  };

  const filteredEvents = filter === 'all' 
    ? events 
    : events.filter(e => e.event_type === filter);

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50">
        <Navbar />
        <div className="flex justify-center items-center py-20">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-teal-600"></div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <Navbar />

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <div className="text-center mb-12">
          <h1 className="text-4xl font-bold text-gray-900 mb-4">
            이벤트 & 행사
          </h1>
          <p className="text-lg text-gray-600">
            다양한 이벤트에 참여하고 달란트를 적립하세요!
          </p>
        </div>

        <div className="flex justify-center gap-3 mb-8">
          <button
            onClick={() => setFilter('all')}
            className={`px-4 py-2 rounded-lg font-medium transition-colors whitespace-nowrap ${
              filter === 'all'
                ? 'bg-teal-600 text-white'
                : 'bg-white text-gray-700 hover:bg-gray-50'
            }`}
          >
            전체
          </button>
          <button
            onClick={() => setFilter('attendance')}
            className={`px-4 py-2 rounded-lg font-medium transition-colors whitespace-nowrap ${
              filter === 'attendance'
                ? 'bg-teal-600 text-white'
                : 'bg-white text-gray-700 hover:bg-gray-50'
            }`}
          >
            출석
          </button>
          <button
            onClick={() => setFilter('participation')}
            className={`px-4 py-2 rounded-lg font-medium transition-colors whitespace-nowrap ${
              filter === 'participation'
                ? 'bg-teal-600 text-white'
                : 'bg-white text-gray-700 hover:bg-gray-50'
            }`}
          >
            참여
          </button>
          <button
            onClick={() => setFilter('special')}
            className={`px-4 py-2 rounded-lg font-medium transition-colors whitespace-nowrap ${
              filter === 'special'
                ? 'bg-teal-600 text-white'
                : 'bg-white text-gray-700 hover:bg-gray-50'
            }`}
          >
            특별 이벤트
          </button>
        </div>

        {filteredEvents.length === 0 ? (
          <div className="text-center py-20">
            <i className="ri-calendar-event-line text-6xl text-gray-300 mb-4"></i>
            <p className="text-gray-500 text-lg">진행 중인 이벤트가 없습니다.</p>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {filteredEvents.map((event) => {
              const participated = hasParticipated(event.id);
              const isFull = event.max_participants && event.current_participants >= event.max_participants;

              return (
                <div
                  key={event.id}
                  className="bg-white rounded-lg shadow-sm overflow-hidden hover:shadow-md transition-shadow"
                >
                  <div className="p-6">
                    <div className="flex items-start justify-between mb-3">
                      <span className={`px-3 py-1 text-xs font-medium rounded-full whitespace-nowrap ${getEventTypeColor(event.event_type)}`}>
                        {getEventTypeLabel(event.event_type)}
                      </span>
                      {participated && (
                        <span className="px-3 py-1 text-xs font-medium rounded-full bg-gray-100 text-gray-800 whitespace-nowrap">
                          참여 완료
                        </span>
                      )}
                    </div>

                    <h3 className="text-xl font-bold text-gray-900 mb-2">
                      {event.title}
                    </h3>

                    {event.description && (
                      <p className="text-gray-600 text-sm mb-4 line-clamp-2">
                        {event.description}
                      </p>
                    )}

                    <div className="space-y-2 mb-4">
                      <div className="flex items-center text-sm text-gray-600">
                        <i className="ri-gift-line w-5 h-5 flex items-center justify-center mr-2 text-teal-600"></i>
                        <span className="font-medium text-teal-600">
                          {event.talent_reward.toLocaleString()} 달란트
                        </span>
                      </div>

                      <div className="flex items-center text-sm text-gray-600">
                        <i className="ri-calendar-line w-5 h-5 flex items-center justify-center mr-2"></i>
                        <span>
                          {new Date(event.start_date).toLocaleDateString()} ~ {new Date(event.end_date).toLocaleDateString()}
                        </span>
                      </div>

                      {event.max_participants && (
                        <div className="flex items-center text-sm text-gray-600">
                          <i className="ri-group-line w-5 h-5 flex items-center justify-center mr-2"></i>
                          <span>
                            {event.current_participants} / {event.max_participants}명
                          </span>
                        </div>
                      )}
                    </div>

                    <button
                      onClick={() => handleParticipate(event)}
                      disabled={participated || isFull}
                      className={`w-full py-2 rounded-lg font-medium transition-colors whitespace-nowrap ${
                        participated || isFull
                          ? 'bg-gray-100 text-gray-400 cursor-not-allowed'
                          : 'bg-teal-600 text-white hover:bg-teal-700'
                      }`}
                    >
                      {participated ? '참여 완료' : isFull ? '마감' : '참여하기'}
                    </button>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>

      <Footer />
    </div>
  );
}
